import type { VideoGenerationPointCostParams } from "./pointCosts"
import { useSessionStore } from "~/store/session"
import { ensureSufficientPoints } from "~/store/pointBalance"

export type PointRefresh = "balance" | "user"

export type PointActionOptions = {
  videoOptions?: VideoGenerationPointCostParams
  isStale?: () => boolean
  refresh?: PointRefresh
}

// 未通过校验、失效或重复调用返回 undefined；操作本身的返回值和异常原样传递。
export type PointActionRunner = <T>(
  cost: number,
  action: () => Promise<T>,
  options?: PointActionOptions,
) => Promise<T | undefined>

// 刷新失败不能覆盖已提交任务的结果，也不能产生未处理的 rejection。
export const refreshPoints = async (
  refresh: PointRefresh = "balance",
): Promise<void> => {
  try {
    const session = useSessionStore.getState()
    await (refresh === "user"
      ? session.refreshUser()
      : session.refreshPointBalance())
  } catch {
    // 下次校验仍会拉取最新余额。
  }
}

// 包裹扣费请求或整段轮询，不能包裹单次轮询查询。
// 无论成功或异常都刷新，以覆盖请求已扣费但响应丢失、任务失败退款等情况。
export const withPointBalanceRefresh = async <T>(
  action: () => Promise<T>,
  refresh: PointRefresh = "balance",
): Promise<T> => {
  try {
    return await action()
  } finally {
    void refreshPoints(refresh)
  }
}

export const createPointActionRunner = (
  guard: (
    cost: number,
    videoOptions?: VideoGenerationPointCostParams,
  ) => Promise<boolean>,
): PointActionRunner => {
  let running = false

  return async (cost, action, options = {}) => {
    if (running || options.isStale?.()) return undefined
    running = true
    try {
      if (!(await guard(cost, options.videoOptions)) || options.isStale?.())
        return undefined

      return await withPointBalanceRefresh(action, options.refresh)
    } finally {
      running = false
    }
  }
}

export const runPointAction = createPointActionRunner(ensureSufficientPoints)
